import type { FontPreset, ResolvedMode, ThemeDeclaration, ThemeMode } from '@blufin/theme';
import { Font, SuspendTransitions, ThemeAttributes } from '@blufin/theme';
import * as React from 'react';
import { AppRuntime } from '../../utils/AppRuntime/AppRuntime';
import { SYSTEM_KEY_PREFIX, SystemLocalStorage } from '../../utils/SystemLocalStorage/SystemLocalStorage';
import { ThemeGuards } from '../../utils/ThemeGuards/ThemeGuards';

/** Possible UI density settings. */
export type Density = 'compact' | 'spacious' | 'standard';

export interface AvailableTheme {
  displayName: string;
  name: string;
}

export interface ThemeContextValue {
  available: AvailableTheme[];
  current: string;
  /** Cycle through densities in compact → standard → spacious → compact order. */
  cycleDensity: () => void;
  density: Density;
  /** Active font preset — a fourth appearance axis, independent of theme, mode and density. */
  font: FontPreset;
  /** User-level panel style — on floats the app's sidebars as `inset` cards, off docks them edge to edge. */
  insetPanels: boolean;
  mode: ThemeMode;
  /** User-level "force reduced motion" — on adds reduction, off falls back to the OS preference. */
  reduceMotion: boolean;
  resolvedMode: ResolvedMode;
  setDensity: (density: Density) => void;
  setFont: (font: FontPreset) => void;
  setInsetPanels: (on: boolean) => void;
  setMode: (mode: ThemeMode) => void;
  setReduceMotion: (on: boolean) => void;
  setTheme: (name: string) => void;
}

export const ThemeContext = React.createContext<ThemeContextValue | undefined>(undefined);

export interface ThemeProviderProps {
  children: React.ReactNode;
  initialDensity?: Density;
  initialFont?: FontPreset;
  initialInsetPanels?: boolean;
  initialMode: ThemeMode;
  initialReduceMotion?: boolean;
  initialTheme: string;
  themes: ThemeDeclaration[];
}

const STORAGE_KEY_DENSITY = 'density';
const STORAGE_KEY_FONT = 'font';
const STORAGE_KEY_INSET_PANELS = 'insetPanels';
const STORAGE_KEY_MODE = 'mode';
const STORAGE_KEY_REDUCE_MOTION = 'reduceMotion';
const STORAGE_KEY_THEME = 'theme';

const DENSITY_CLASSES: readonly string[] = ['density-compact', 'density-spacious', 'density-standard'];
const DENSITIES: readonly Density[] = ['compact', 'standard', 'spacious'];
/** Derived from `Font.ids` so a newly added preset can never be forgotten here. */
const FONT_CLASSES: readonly string[] = Font.ids.map(id => `font-${id}`);

/**
 * Apply the density-{value} class to <html>, removing any prior density class.
 * Wrapped in SuspendTransitions so the swap paints instantly instead of
 * animating spacing/font-size through every `transition*` rule.
 * @param density - The density level to apply ('compact' | 'standard' | 'spacious').
 */
const _applyDensityClass = (density: Density): void => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  SuspendTransitions.run(() => {
    for (const cls of DENSITY_CLASSES) root.classList.remove(cls);
    root.classList.add(`density-${density}`);
  });
};

/**
 * Apply the font-{value} class to <html>, removing any prior font class.
 * Wrapped in SuspendTransitions so the typeface swap paints instantly instead of
 * animating through every `transition*` rule.
 * @param font - The font preset to apply.
 */
const _applyFontClass = (font: FontPreset): void => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  SuspendTransitions.run(() => {
    for (const cls of FONT_CLASSES) root.classList.remove(cls);
    root.classList.add(`font-${font}`);
  });
};

const _readStoredDensity = (fallback: Density): Density => {
  const raw = SystemLocalStorage.get(STORAGE_KEY_DENSITY);
  return ThemeGuards.isDensity(raw) ? raw : fallback;
};

/**
 * Reads the persisted font preset. Unlike density/mode this warns on an unrecognized non-null value:
 * silently ignoring it would revert the user's whole app to another typeface with no explanation.
 * @param fallback - Value to use when nothing valid is stored.
 */
const _readStoredFont = (fallback: FontPreset): FontPreset => {
  const raw = SystemLocalStorage.get(STORAGE_KEY_FONT);
  if (raw === null) return fallback;
  if (ThemeGuards.isFont(raw)) return raw;
  // eslint-disable-next-line no-console -- `_readStoredFont` is a module-scope storage reader, so hooks are illegal; warns that a persisted font preset is not recognized and the fallback was used.
  console.warn(`[ThemeProvider] ignoring unknown font '${raw}'`);
  return fallback;
};

/**
 * Reads the persisted inset-panels flag. `getJSON`, like `_readStoredReduceMotion`, so the stored text is the
 * JSON literal `true`/`false`; anything else (absent, junk, a non-boolean) falls back.
 * @param fallback - Value to use when nothing valid is stored.
 */
const _readStoredInsetPanels = (fallback: boolean): boolean => {
  const raw = SystemLocalStorage.getJSON(STORAGE_KEY_INSET_PANELS);
  return typeof raw === 'boolean' ? raw : fallback;
};

const _readStoredMode = (fallback: ThemeMode): ThemeMode => {
  const raw = SystemLocalStorage.get(STORAGE_KEY_MODE);
  return ThemeGuards.isThemeMode(raw) ? raw : fallback;
};

/**
 * Reads the persisted reduce-motion flag. `getJSON` (not `get`) so the stored text is the JSON literal
 * `true`/`false` — exactly what `PreMountScript` compares against before first paint. Anything else
 * (absent, junk, a non-boolean) falls back, so a corrupt value can never silently kill motion.
 * @param fallback - Value to use when nothing valid is stored.
 */
const _readStoredReduceMotion = (fallback: boolean): boolean => {
  const raw = SystemLocalStorage.getJSON(STORAGE_KEY_REDUCE_MOTION);
  return typeof raw === 'boolean' ? raw : fallback;
};

const _readStoredTheme = (fallback: string, themes: ThemeDeclaration[]): string => {
  const raw = SystemLocalStorage.get(STORAGE_KEY_THEME);
  if (raw === null) return fallback;
  if (themes.some(t => t.name === raw)) return raw;
  // eslint-disable-next-line no-console -- `_readStoredTheme` is a module-scope storage reader, so hooks are illegal; warns that a persisted theme name no longer exists and the fallback was used.
  console.warn(`[ThemeProvider] ignoring unknown theme '${raw}'`);
  return fallback;
};

const _resolveMode = (mode: ThemeMode, systemPref: ResolvedMode): ResolvedMode => {
  return mode === 'system' ? systemPref : mode;
};

const _useIsoLayoutEffect = typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

/**
 * Provides theme + mode + density + font + reduce-motion + inset-panels state to the React tree. **Local-first**:
 * `localStorage` (via `SystemLocalStorage` under the `app_*` namespace) is the source of truth for
 * `mode`, `density`, `theme`, `font`, `reduceMotion` and `insetPanels`. Initial values are read synchronously in
 * `useState` initializers (no FOUC); every setter writes back to storage. A `window.addEventListener('storage', ...)`
 * listener propagates changes between sibling Electron windows. Cloud sync (if any) is a
 * one-way mirror layered on top — failed network writes never revert the UI.
 * `insetPanels` is the one axis with no `<html>` attribute: it picks component VARIANTS (the app's `Sidebar`,
 * `SidebarRight` and `AppLayoutBeta`), which the app reads through `useInsetPanels` and passes down as props,
 * and the synchronous initializer is what keeps the first frame on the stored layout.
 * `initialMode` / `initialTheme` / `initialDensity` / `initialFont` / `initialReduceMotion` / `initialInsetPanels`
 * props are fallbacks used only when the corresponding `localStorage` key is absent or holds an invalid value.
 * @param props - Provider configuration.
 * @param props.children - Subtree to render under the provider.
 * @param props.initialDensity - Fallback density when `app_density` is absent or invalid.
 * @param props.initialFont - Fallback font preset when `app_font` is absent or invalid.
 * @param props.initialInsetPanels - Fallback inset-panels flag when `app_insetPanels` is absent or invalid.
 * @param props.initialMode - Fallback mode when `app_mode` is absent or invalid.
 * @param props.initialReduceMotion - Fallback reduce-motion flag when `app_reduceMotion` is absent or invalid.
 * @param props.initialTheme - Fallback theme when `app_theme` is absent or invalid.
 * @param props.themes - Themes available to consumers.
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  initialDensity = 'standard',
  initialFont = Font.default,
  initialInsetPanels = true,
  initialMode,
  initialReduceMotion = false,
  initialTheme,
  themes
}) => {
  if (themes.length === 0) throw new Error('ThemeProvider requires at least one theme.');
  const [current, setCurrent] = React.useState<string>(() => _readStoredTheme(initialTheme, themes));
  const [mode, setModeState] = React.useState<ThemeMode>(() => _readStoredMode(initialMode));
  const [density, setDensityState] = React.useState<Density>(() => _readStoredDensity(initialDensity));
  const [font, setFontState] = React.useState<FontPreset>(() => _readStoredFont(initialFont));
  const [reduceMotion, setReduceMotionState] = React.useState<boolean>(() =>
    _readStoredReduceMotion(initialReduceMotion)
  );
  const [insetPanels, setInsetPanelsState] = React.useState<boolean>(() => _readStoredInsetPanels(initialInsetPanels));
  /**
   * Start with 'light' to avoid hydration mismatch — `matchMedia` is client-only.
   * The real system preference is resolved in a post-mount useEffect.
   */
  const [systemPref, setSystemPref] = React.useState<ResolvedMode>('light');
  const resolvedMode: ResolvedMode = _resolveMode(mode, systemPref);
  const _modeRef = React.useRef(mode);
  const _densityRef = React.useRef(density);
  const _fontRef = React.useRef(font);
  const _currentRef = React.useRef(current);
  const _reduceMotionRef = React.useRef(reduceMotion);
  const _insetPanelsRef = React.useRef(insetPanels);
  _modeRef.current = mode;
  _densityRef.current = density;
  _fontRef.current = font;
  _currentRef.current = current;
  _reduceMotionRef.current = reduceMotion;
  _insetPanelsRef.current = insetPanels;
  /** Sync `[data-theme]` / `[data-mode]` attributes to current state before paint to avoid a flash. */
  _useIsoLayoutEffect(() => {
    ThemeAttributes.setTheme(current);
  }, [current]);
  _useIsoLayoutEffect(() => {
    ThemeAttributes.setMode(resolvedMode);
  }, [resolvedMode]);
  /** Sync density class to <html> before paint. */
  _useIsoLayoutEffect(() => {
    _applyDensityClass(density);
  }, [density]);
  /** Sync font class to <html> before paint. */
  _useIsoLayoutEffect(() => {
    _applyFontClass(font);
  }, [font]);
  /** Sync `[data-reduce-motion]` before paint — the global rule in `@blufin/theme`'s reset does the rest. */
  _useIsoLayoutEffect(() => {
    ThemeAttributes.setReduceMotion(reduceMotion);
  }, [reduceMotion]);
  /** Resolve system preference post-mount to avoid hydration mismatch. */
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    setSystemPref(AppRuntime.getSystemThemePreference());
  }, []);
  /** Listen for system preference changes (drives `mode === 'system'`). */
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent): void => {
      setSystemPref(e.matches ? 'dark' : 'light');
    };
    mediaQuery.addEventListener('change', handleChange);
    return (): void => mediaQuery.removeEventListener('change', handleChange);
  }, []);
  /** Cross-window sync: react to `localStorage` mutations from sibling Electron renderers. */
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (e: StorageEvent): void => {
      if (e.key === null) return;
      if (e.key === `${SYSTEM_KEY_PREFIX}${STORAGE_KEY_MODE}`) {
        const next = _readStoredMode(_modeRef.current);
        if (next !== _modeRef.current) setModeState(next);
      } else if (e.key === `${SYSTEM_KEY_PREFIX}${STORAGE_KEY_DENSITY}`) {
        const next = _readStoredDensity(_densityRef.current);
        if (next !== _densityRef.current) setDensityState(next);
      } else if (e.key === `${SYSTEM_KEY_PREFIX}${STORAGE_KEY_FONT}`) {
        const next = _readStoredFont(_fontRef.current);
        if (next !== _fontRef.current) setFontState(next);
      } else if (e.key === `${SYSTEM_KEY_PREFIX}${STORAGE_KEY_INSET_PANELS}`) {
        const next = _readStoredInsetPanels(_insetPanelsRef.current);
        if (next !== _insetPanelsRef.current) setInsetPanelsState(next);
      } else if (e.key === `${SYSTEM_KEY_PREFIX}${STORAGE_KEY_REDUCE_MOTION}`) {
        const next = _readStoredReduceMotion(_reduceMotionRef.current);
        if (next !== _reduceMotionRef.current) setReduceMotionState(next);
      } else if (e.key === `${SYSTEM_KEY_PREFIX}${STORAGE_KEY_THEME}`) {
        const next = _readStoredTheme(_currentRef.current, themes);
        if (next !== _currentRef.current) setCurrent(next);
      }
    };
    window.addEventListener('storage', handler);
    return (): void => window.removeEventListener('storage', handler);
  }, [themes]);
  const setMode = React.useCallback((next: ThemeMode): void => {
    SystemLocalStorage.set(STORAGE_KEY_MODE, next);
    setModeState(next);
  }, []);
  const setTheme = React.useCallback((name: string): void => {
    SystemLocalStorage.set(STORAGE_KEY_THEME, name);
    setCurrent(name);
  }, []);
  const setDensity = React.useCallback((next: Density): void => {
    SystemLocalStorage.set(STORAGE_KEY_DENSITY, next);
    setDensityState(next);
  }, []);
  /** `set`, not `setJSON` — the stored text is a plain preset id, exactly what `PreMountScript` compares. */
  const setFont = React.useCallback((next: FontPreset): void => {
    SystemLocalStorage.set(STORAGE_KEY_FONT, next);
    setFontState(next);
  }, []);
  /** `setJSON`, not `set` — the stored text must be the JSON literal `true`/`false` that `PreMountScript` reads. */
  const setReduceMotion = React.useCallback((on: boolean): void => {
    SystemLocalStorage.setJSON(STORAGE_KEY_REDUCE_MOTION, on);
    setReduceMotionState(on);
  }, []);
  /** `setJSON`, the same as `setReduceMotion` — `_readStoredInsetPanels` accepts only the JSON literal. */
  const setInsetPanels = React.useCallback((on: boolean): void => {
    SystemLocalStorage.setJSON(STORAGE_KEY_INSET_PANELS, on);
    setInsetPanelsState(on);
  }, []);
  const cycleDensity = React.useCallback((): void => {
    setDensityState(prev => {
      const idx = DENSITIES.indexOf(prev);
      const next = DENSITIES[(idx + 1) % DENSITIES.length];
      SystemLocalStorage.set(STORAGE_KEY_DENSITY, next);
      return next;
    });
  }, []);
  const available = React.useMemo<AvailableTheme[]>(
    () => themes.map(t => ({ displayName: t.displayName ?? t.name, name: t.name })),
    [themes]
  );
  const value = React.useMemo<ThemeContextValue>(
    () => ({
      available,
      current,
      cycleDensity,
      density,
      font,
      insetPanels,
      mode,
      reduceMotion,
      resolvedMode,
      setDensity,
      setFont,
      setInsetPanels,
      setMode,
      setReduceMotion,
      setTheme
    }),
    [
      available,
      current,
      cycleDensity,
      density,
      font,
      insetPanels,
      mode,
      reduceMotion,
      resolvedMode,
      setDensity,
      setFont,
      setInsetPanels,
      setMode,
      setReduceMotion,
      setTheme
    ]
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
